import {
  PartnershipCreator,
  PartnershipModpack,
  Partnership,
  PartnershipFull,
  PartnershipCreatorCode,
  PartnershipIncentive,
  PartnershipNote,
  PartnershipComment,
  PartnershipTag,
  PartnershipServerLink,
  PartnershipActivityLogEntry,
  PartnershipStats,
  CreatorFull,
  ModpackFull,
  PaginatedResponse,
  CreateCreatorRequest,
  UpdateCreatorRequest,
  CreateModpackRequest,
  UpdateModpackRequest,
  CreatePartnershipRequest,
  UpdatePartnershipRequest,
  CreateCreatorCodeRequest,
  UpdateCreatorCodeRequest,
  CreateIncentiveRequest,
  UpdateIncentiveRequest,
  CreateNoteRequest,
  UpdateNoteRequest,
  CreateServerLinkRequest,
  UpdateServerLinkRequest,
  CreateTagRequest,
  UpdateTagRequest,
  PartnershipListParams,
  PartnershipStatus,
  CreatorType,
  PlatformType,
  ServerLinkStatus,
} from '../types/partnership';
import { CalendarEvent } from '../types/calendar';

const PARTNERSHIPS_API_BASE_URL = 'https://spy.playcdu.co/api/notes/partnerships';

class PartnershipsApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${PARTNERSHIPS_API_BASE_URL}${endpoint}`, {
      ...options,
      // Cloudflare Access handles authentication via cookies
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  private buildQueryString(params: Record<string, string | number | boolean | undefined | null>): string {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        qs.append(key, String(value));
      }
    }
    const str = qs.toString();
    return str ? `?${str}` : '';
  }

  // ── Creator Endpoints ───────────────────────────────────────────────────

  async createCreator(data: CreateCreatorRequest): Promise<{ ok: boolean; id: string }> {
    return this.request('/creators', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getAllCreators(params?: {
    type?: CreatorType;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<PartnershipCreator> | PartnershipCreator[]> {
    const qs = this.buildQueryString(params || {});
    return this.request(`/creators${qs}`);
  }

  async searchCreators(q: string, page?: number, limit?: number): Promise<PartnershipCreator[]> {
    const qs = this.buildQueryString({ q, page, limit });
    return this.request(`/creators/search${qs}`);
  }

  async getCreatorCount(type?: CreatorType): Promise<{ count: number }> {
    const qs = this.buildQueryString({ type });
    return this.request(`/creators/count${qs}`);
  }

  async getCreatorById(id: string): Promise<PartnershipCreator> {
    return this.request(`/creators/${encodeURIComponent(id)}`);
  }

  async getCreatorFull(id: string): Promise<CreatorFull> {
    return this.request(`/creators/${encodeURIComponent(id)}/full`);
  }

  async getCreatorPartnerships(id: string): Promise<Partnership[]> {
    return this.request(`/creators/${encodeURIComponent(id)}/partnerships`);
  }

  async getCreatorCodes(id: string): Promise<PartnershipCreatorCode[]> {
    return this.request(`/creators/${encodeURIComponent(id)}/codes`);
  }

  async updateCreator(id: string, data: UpdateCreatorRequest): Promise<PartnershipCreator> {
    return this.request(`/creators/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteCreator(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/creators/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // ── Modpack Endpoints ───────────────────────────────────────────────────

  async createModpack(data: CreateModpackRequest): Promise<{ ok: boolean; id: string }> {
    return this.request('/modpacks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getAllModpacks(params?: {
    platform_type?: PlatformType;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<PartnershipModpack> | PartnershipModpack[]> {
    const qs = this.buildQueryString(params || {});
    return this.request(`/modpacks${qs}`);
  }

  async searchModpacks(q: string, page?: number, limit?: number): Promise<PartnershipModpack[]> {
    const qs = this.buildQueryString({ q, page, limit });
    return this.request(`/modpacks/search${qs}`);
  }

  async getModpackCount(creator_id?: string): Promise<{ count: number }> {
    const qs = this.buildQueryString({ creator_id });
    return this.request(`/modpacks/count${qs}`);
  }

  async getModpacksByCreator(
    creatorId: string,
    page?: number,
    limit?: number
  ): Promise<PartnershipModpack[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/modpacks/creator/${encodeURIComponent(creatorId)}${qs}`);
  }

  async getModpackById(id: string): Promise<PartnershipModpack> {
    return this.request(`/modpacks/${encodeURIComponent(id)}`);
  }

  async getModpackFull(id: string): Promise<ModpackFull> {
    return this.request(`/modpacks/${encodeURIComponent(id)}/full`);
  }

  async updateModpack(id: string, data: UpdateModpackRequest): Promise<PartnershipModpack> {
    return this.request(`/modpacks/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteModpack(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/modpacks/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // ── Partnership Core Endpoints ──────────────────────────────────────────

  async createPartnership(data: CreatePartnershipRequest): Promise<PartnershipFull> {
    return this.request('', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getAllPartnerships(
    params?: PartnershipListParams
  ): Promise<PaginatedResponse<Partnership>> {
    const qs = this.buildQueryString(params as Record<string, string | number | boolean | undefined> || {});
    return this.request(qs || '');
  }

  async searchPartnerships(
    q: string,
    page?: number,
    limit?: number
  ): Promise<Partnership[]> {
    const qs = this.buildQueryString({ q, page, limit });
    return this.request(`/search${qs}`);
  }

  async getPartnershipStats(): Promise<PartnershipStats> {
    return this.request('/stats');
  }

  async getPartnershipCount(params?: {
    status?: PartnershipStatus;
    assigned_to?: string;
  }): Promise<{ count: number }> {
    const qs = this.buildQueryString(params || {});
    return this.request(`/count${qs}`);
  }

  async getPartnershipsByAssignee(uuid: string): Promise<Partnership[]> {
    return this.request(`/assignee/${encodeURIComponent(uuid)}`);
  }

  async getPartnershipsByStatus(status: PartnershipStatus): Promise<Partnership[]> {
    return this.request(`/status/${encodeURIComponent(status)}`);
  }

  async getPartnershipsByModpack(modpackId: string): Promise<Partnership[]> {
    return this.request(`/modpack/${encodeURIComponent(modpackId)}`);
  }

  async getOfInterestPartnerships(params?: {
    assigned_to?: string;
    page?: number;
    limit?: number;
  }): Promise<Partnership[]> {
    const qs = this.buildQueryString(params || {});
    return this.request(`/of-interest${qs}`);
  }

  async getOnHoldPartnerships(params?: {
    assigned_to?: string;
    page?: number;
    limit?: number;
  }): Promise<Partnership[]> {
    const qs = this.buildQueryString(params || {});
    return this.request(`/on-hold${qs}`);
  }

  async getPartnershipById(id: string): Promise<PartnershipFull> {
    return this.request(`/${encodeURIComponent(id)}`);
  }

  async updatePartnership(id: string, data: UpdatePartnershipRequest): Promise<Partnership> {
    return this.request(`/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async changePartnershipStatus(
    id: string,
    status: PartnershipStatus,
    reason?: string
  ): Promise<Partnership> {
    return this.request(`/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, reason }),
    });
  }

  async assignPartnership(id: string, assigned_to: string | null): Promise<Partnership> {
    return this.request(`/${encodeURIComponent(id)}/assign`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assigned_to }),
    });
  }

  async changePartnershipPriority(id: string, priority: number): Promise<Partnership> {
    return this.request(`/${encodeURIComponent(id)}/priority`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priority }),
    });
  }

  async deletePartnership(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // ── Tag Endpoints ───────────────────────────────────────────────────────

  async createTag(data: CreateTagRequest): Promise<PartnershipTag> {
    return this.request('/tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getAllTags(): Promise<PartnershipTag[]> {
    return this.request('/tags');
  }

  async getTagById(id: string): Promise<PartnershipTag> {
    return this.request(`/tags/${encodeURIComponent(id)}`);
  }

  async getPartnershipsByTag(tagId: string): Promise<Partnership[]> {
    return this.request(`/tags/${encodeURIComponent(tagId)}/partnerships`);
  }

  async updateTag(id: string, data: UpdateTagRequest): Promise<PartnershipTag> {
    return this.request(`/tags/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteTag(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/tags/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async addTagToPartnership(
    partnershipId: string,
    tagId: string
  ): Promise<{ ok: boolean }> {
    return this.request(
      `/${encodeURIComponent(partnershipId)}/tags/${encodeURIComponent(tagId)}`,
      { method: 'POST' }
    );
  }

  async removeTagFromPartnership(
    partnershipId: string,
    tagId: string
  ): Promise<{ ok: boolean }> {
    return this.request(
      `/${encodeURIComponent(partnershipId)}/tags/${encodeURIComponent(tagId)}`,
      { method: 'DELETE' }
    );
  }

  async getTagsForPartnership(partnershipId: string): Promise<PartnershipTag[]> {
    return this.request(`/${encodeURIComponent(partnershipId)}/tags`);
  }

  // ── Note Endpoints ──────────────────────────────────────────────────────

  async createNote(
    partnershipId: string,
    data: CreateNoteRequest
  ): Promise<PartnershipNote> {
    return this.request(`/${encodeURIComponent(partnershipId)}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getNotesForPartnership(
    partnershipId: string,
    page?: number,
    limit?: number
  ): Promise<PartnershipNote[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/${encodeURIComponent(partnershipId)}/notes${qs}`);
  }

  async getPinnedNotes(partnershipId: string): Promise<PartnershipNote[]> {
    return this.request(`/${encodeURIComponent(partnershipId)}/notes/pinned`);
  }

  async getNoteById(noteId: string): Promise<PartnershipNote> {
    return this.request(`/notes/${encodeURIComponent(noteId)}`);
  }

  async updateNote(noteId: string, data: UpdateNoteRequest): Promise<PartnershipNote> {
    return this.request(`/notes/${encodeURIComponent(noteId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async pinNote(noteId: string, pinned: boolean): Promise<PartnershipNote> {
    return this.request(`/notes/${encodeURIComponent(noteId)}/pin`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinned }),
    });
  }

  async deleteNote(noteId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/notes/${encodeURIComponent(noteId)}`, {
      method: 'DELETE',
    });
  }

  // ── Comment Endpoints ───────────────────────────────────────────────────

  async createComment(
    partnershipId: string,
    content: string
  ): Promise<PartnershipComment> {
    return this.request(`/${encodeURIComponent(partnershipId)}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
  }

  async getCommentsForPartnership(
    partnershipId: string,
    page?: number,
    limit?: number
  ): Promise<PartnershipComment[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/${encodeURIComponent(partnershipId)}/comments${qs}`);
  }

  async getCommentById(commentId: string): Promise<PartnershipComment> {
    return this.request(`/comments/${encodeURIComponent(commentId)}`);
  }

  async deleteComment(commentId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/comments/${encodeURIComponent(commentId)}`, {
      method: 'DELETE',
    });
  }

  // ── Creator Code Endpoints ──────────────────────────────────────────────

  async createCreatorCode(
    partnershipId: string,
    data: CreateCreatorCodeRequest
  ): Promise<PartnershipCreatorCode> {
    return this.request(`/${encodeURIComponent(partnershipId)}/codes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getCodesForPartnership(partnershipId: string): Promise<PartnershipCreatorCode[]> {
    return this.request(`/${encodeURIComponent(partnershipId)}/codes`);
  }

  async getAllCodes(): Promise<PartnershipCreatorCode[]> {
    return this.request('/codes');
  }

  async getCodeCount(): Promise<{ count: number }> {
    return this.request('/codes/count');
  }

  async lookupCode(code: string): Promise<PartnershipCreatorCode> {
    return this.request(`/codes/lookup/${encodeURIComponent(code)}`);
  }

  async getCodeById(codeId: string): Promise<PartnershipCreatorCode> {
    return this.request(`/codes/${encodeURIComponent(codeId)}`);
  }

  async updateCode(
    codeId: string,
    data: UpdateCreatorCodeRequest
  ): Promise<PartnershipCreatorCode> {
    return this.request(`/codes/${encodeURIComponent(codeId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteCode(codeId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/codes/${encodeURIComponent(codeId)}`, {
      method: 'DELETE',
    });
  }

  async linkCodeToPartnership(codeId: string, partnershipId: string): Promise<{ ok: boolean }> {
    return this.request(
      `/codes/${encodeURIComponent(codeId)}/link/${encodeURIComponent(partnershipId)}`,
      { method: 'POST' }
    );
  }

  async unlinkCodeFromPartnership(codeId: string, partnershipId: string): Promise<{ ok: boolean }> {
    return this.request(
      `/codes/${encodeURIComponent(codeId)}/unlink/${encodeURIComponent(partnershipId)}`,
      { method: 'DELETE' }
    );
  }

  async getPartnershipsForCode(codeId: string): Promise<Partnership[]> {
    return this.request(`/codes/${encodeURIComponent(codeId)}/partnerships`);
  }

  // ── Incentive Endpoints ─────────────────────────────────────────────────

  async createIncentive(
    partnershipId: string,
    data: CreateIncentiveRequest
  ): Promise<PartnershipIncentive> {
    return this.request(`/${encodeURIComponent(partnershipId)}/incentives`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getIncentivesForPartnership(
    partnershipId: string
  ): Promise<PartnershipIncentive[]> {
    return this.request(`/${encodeURIComponent(partnershipId)}/incentives`);
  }

  async getAllIncentives(): Promise<PartnershipIncentive[]> {
    return this.request('/incentives');
  }

  async getIncentiveCount(): Promise<{ count: number }> {
    return this.request('/incentives/count');
  }

  async getIncentiveById(incentiveId: string): Promise<PartnershipIncentive> {
    return this.request(`/incentives/${encodeURIComponent(incentiveId)}`);
  }

  async updateIncentive(
    incentiveId: string,
    data: UpdateIncentiveRequest
  ): Promise<PartnershipIncentive> {
    return this.request(`/incentives/${encodeURIComponent(incentiveId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteIncentive(
    incentiveId: string
  ): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/incentives/${encodeURIComponent(incentiveId)}`, {
      method: 'DELETE',
    });
  }

  // ── Server Link Endpoints ───────────────────────────────────────────────

  async createServerLink(
    partnershipId: string,
    data: CreateServerLinkRequest
  ): Promise<PartnershipServerLink> {
    return this.request(`/${encodeURIComponent(partnershipId)}/servers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getServerLinksForPartnership(
    partnershipId: string
  ): Promise<PartnershipServerLink[]> {
    return this.request(`/${encodeURIComponent(partnershipId)}/servers`);
  }

  async getAllServerLinks(params?: {
    status?: ServerLinkStatus;
    page?: number;
    limit?: number;
  }): Promise<PartnershipServerLink[]> {
    const qs = this.buildQueryString(params || {});
    return this.request(`/servers${qs}`);
  }

  async searchServerLinks(
    q: string,
    page?: number,
    limit?: number
  ): Promise<PartnershipServerLink[]> {
    const qs = this.buildQueryString({ q, page, limit });
    return this.request(`/servers/search${qs}`);
  }

  async getServerLinkCount(status?: ServerLinkStatus): Promise<{ count: number }> {
    const qs = this.buildQueryString({ status });
    return this.request(`/servers/count${qs}`);
  }

  async getPartnershipsByFqdn(fqdn: string): Promise<Partnership[]> {
    return this.request(`/servers/fqdn/${encodeURIComponent(fqdn)}`);
  }

  async getPartnershipsByCluster(clusterId: string): Promise<Partnership[]> {
    return this.request(`/servers/cluster/${encodeURIComponent(clusterId)}`);
  }

  async updateServerLink(
    linkId: string,
    data: UpdateServerLinkRequest
  ): Promise<PartnershipServerLink> {
    return this.request(`/servers/${encodeURIComponent(linkId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteServerLink(
    linkId: string
  ): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/servers/${encodeURIComponent(linkId)}`, {
      method: 'DELETE',
    });
  }

  // ── Calendar Link Endpoints ─────────────────────────────────────────────

  async linkCalendarEvent(
    partnershipId: string,
    eventId: string
  ): Promise<{ ok: boolean }> {
    return this.request(
      `/${encodeURIComponent(partnershipId)}/calendar/${encodeURIComponent(eventId)}`,
      { method: 'POST' }
    );
  }

  async unlinkCalendarEvent(
    partnershipId: string,
    eventId: string
  ): Promise<{ ok: boolean }> {
    return this.request(
      `/${encodeURIComponent(partnershipId)}/calendar/${encodeURIComponent(eventId)}`,
      { method: 'DELETE' }
    );
  }

  async getCalendarEvents(partnershipId: string): Promise<CalendarEvent[]> {
    return this.request(`/${encodeURIComponent(partnershipId)}/calendar`);
  }

  async getUpcomingCalendarEvents(partnershipId: string): Promise<CalendarEvent[]> {
    return this.request(`/${encodeURIComponent(partnershipId)}/calendar/upcoming`);
  }

  async getPartnershipsByCalendarEvent(eventId: string): Promise<Partnership[]> {
    return this.request(`/calendar/event/${encodeURIComponent(eventId)}`);
  }

  // ── Activity Log Endpoints ──────────────────────────────────────────────

  async getActivityLog(
    partnershipId: string,
    page?: number,
    limit?: number
  ): Promise<PartnershipActivityLogEntry[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/${encodeURIComponent(partnershipId)}/activity${qs}`);
  }

  async getRecentActivity(limit?: number): Promise<PartnershipActivityLogEntry[]> {
    const qs = this.buildQueryString({ limit });
    return this.request(`/activity/recent${qs}`);
  }

  async getActivityByActor(
    actorId: string,
    page?: number,
    limit?: number
  ): Promise<PartnershipActivityLogEntry[]> {
    const qs = this.buildQueryString({ page, limit });
    return this.request(`/activity/by/${encodeURIComponent(actorId)}${qs}`);
  }
}

export const partnershipsApiService = new PartnershipsApiService();
